import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { trackWhatsappClick } from "@/lib/analytics";
import {
  heroPrimaryCtas,
  heroSecondaryCta,
  mainPillars,
  positioning,
} from "@/lib/commercial-content";

const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#ff6b35] noise !pt-32 !pb-16 sm:!pt-36 md:!pt-40 md:!pb-32"
    >
      <div className="absolute inset-0 dot-pattern opacity-15 pointer-events-none" />

      <div
        className="absolute top-24 right-10 w-20 h-20 brutal-border-thick bg-[#0a0a0a] rotate-12 vc-float hidden md:block"
        style={{ "--rot": "12deg" }}
      />
      <div
        className="absolute bottom-20 left-8 w-14 h-14 bg-white brutal-border vc-float hidden md:block"
        style={{ "--rot": "-8deg", animationDelay: "1.4s" }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-10 h-10 bg-[#0a0a0a] rounded-full vc-float hidden lg:block"
        style={{ animationDelay: "0.8s" }}
      />
      <div
        className="absolute -bottom-6 right-32 vc-float hidden lg:block"
        style={{ animationDelay: "2s" }}
      >
        <div
          className="w-0 h-0"
          style={{
            borderLeft: "30px solid transparent",
            borderRight: "30px solid transparent",
            borderBottom: "52px solid #0a0a0a",
          }}
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="vc-reveal in mb-7 flex flex-col items-start gap-2 sm:hidden">
          <div className="font-display text-[#0a0a0a] text-[1.9rem] min-[380px]:text-[2.1rem] uppercase leading-none">
            BEZIER<span className="text-white">CLICK</span>
          </div>
          <p className="font-mono text-[0.68rem] uppercase tracking-widest text-[#0a0a0a]">
            Tecnologia para empresas em Portugal
          </p>
        </div>

        <div
          data-testid="hero-badge"
          className="inline-flex max-w-full items-center gap-2 bg-[#0a0a0a] text-white px-4 py-2 brutal-border vc-reveal in"
        >
          <Sparkles size={14} className="text-[#ff6b35] shrink-0" />
          <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-[#ff6b35] shrink-0" />
          <span className="font-mono uppercase text-[0.66rem] sm:text-xs tracking-widest leading-tight">
            {positioning.eyebrow}
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="vc-hero-title font-display text-[#0a0a0a] mt-6 sm:mt-8 text-[2.2rem] min-[380px]:text-[2.35rem] sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7rem] leading-[0.85] uppercase max-w-[1500px]"
        >
          <span className="md:whitespace-nowrap">Tecnologia para</span>
          <br />
          <span>empresas</span>
          <br />
          <span className="text-outline">venderem,</span>{" "}
          <span className="bg-[#0a0a0a] text-[#ff6b35] px-2 min-[380px]:px-3 inline-block max-w-full -rotate-1 text-[0.74em] sm:text-[0.92em] md:text-[1em] leading-[0.95] whitespace-normal md:whitespace-nowrap break-words [overflow-wrap:anywhere]">
            automatizarem
          </span>
          <br />
          <span className="md:whitespace-nowrap">e operarem melhor.</span>
        </h1>

        <div className="mt-8 sm:mt-10 max-w-2xl">
          <div className="inline-block bg-[#0a0a0a] text-white px-5 py-3 brutal-border-thick">
            <p className="font-medium text-[0.98rem] sm:text-lg leading-snug">
              {positioning.summary}
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3.5 sm:gap-5 items-stretch sm:items-center">
          {heroPrimaryCtas.map((cta, index) => (
            <Link
              key={cta.source}
              data-testid={index === 0 ? "hero-cta-primary" : `hero-cta-${index + 1}`}
              to={cta.target}
              className={`btn-brutal justify-center text-center ${
                index === 1 ? "btn-brutal-orange" : ""
              }`}
            >
              <span>{cta.label}</span>
              <ArrowRight size={18} className="shrink-0" />
            </Link>
          ))}
          {heroSecondaryCta.href ? (
            <a
              data-testid="hero-cta-secondary"
              href={heroSecondaryCta.href}
              onClick={() => trackWhatsappClick("hero_specialist")}
              target="_blank"
              rel="noreferrer"
              className="font-display uppercase text-[0.8rem] sm:text-base text-[#0a0a0a] sm:underline decoration-4 underline-offset-4 hover:text-white transition-colors text-center sm:text-left leading-tight"
            >
              {heroSecondaryCta.label} -&gt;
            </a>
          ) : (
            <Link
              data-testid="hero-cta-secondary"
              to={heroSecondaryCta.target}
              className="font-display uppercase text-[0.8rem] sm:text-base text-[#0a0a0a] sm:underline decoration-4 underline-offset-4 hover:text-white transition-colors text-center sm:text-left leading-tight"
            >
              {heroSecondaryCta.label} -&gt;
            </Link>
          )}
        </div>

        <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {mainPillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <Link
                key={pillar.title}
                to={pillar.href}
                data-testid={`hero-pillar-${index}`}
                className="bg-white brutal-border p-4 sm:p-5 min-w-0 group hover:-translate-y-1 transition-transform"
              >
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="bg-[#0a0a0a] w-11 h-11 flex items-center justify-center brutal-border">
                    <Icon size={22} className="text-[#ff6b35]" />
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-[#0a0a0a] group-hover:translate-x-1 transition-transform shrink-0"
                  />
                </div>
                <h2 className="font-display text-[#0a0a0a] text-3xl sm:text-4xl uppercase leading-none">
                  {pillar.title}
                </h2>
                <p className="font-mono uppercase text-[0.68rem] sm:text-xs tracking-widest text-[#ff6b35] leading-tight mt-2">
                  {pillar.price}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
